/**
 * RecurringPaymentNotice — Aviso exibido no topo de cada aba de pagamento
 * (PIX / Boleto / Cartão) quando a cobrança pertence a uma assinatura
 * recorrente. Explica para o pagador como a recorrência funciona naquele
 * método — PIX/Boleto recebem uma nova fatura antes de cada vencimento;
 * Cartão é debitado automaticamente na fatura do cartão.
 *
 * Componente puro de apresentação. Não faz fetch nem efeitos colaterais.
 * Os textos são literais e os valores formatados vêm de Intl.NumberFormat
 * (saneados por origem).
 *
 * RELEASE: 2026-06-21 v1.2.0 — Tamanho do texto reduzido em ~30% (text-sm → text-[10px]),
 *           título com uppercase tracking-wide, ícone 16→12px, paddings ajustados.
 * RELEASE: 2026-06-21 v1.1.0 — Texto do método "card" reescrito (remove "Ao
 *           confirmar o pagamento com cartão, a" e "sua") e valor em negrito.
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */

import { Info, RefreshCw } from 'lucide-react';

/** Frequência já formatada para exibição ("Mensal", "Trimestral", ...). */
export type FrequenciaLabel = string;

interface Props {
  /** Método de pagamento ativo na aba. */
  method: 'pix' | 'boleto' | 'card';
  /** Frequência amigável já formatada ("Mensal", "Trimestral", ...). */
  frequenciaLabel: FrequenciaLabel;
  /** Valor da recorrência já formatado (ex.: "R$ 54,90"). */
  valorFormatado: string;
}

/**
 * Converte a frequência em uma frase de período (usada nas mensagens).
 * Aceita tanto a forma "Mensal" (label) quanto "mensal" (enum) para
 * tornar a função resiliente caso o caller mude de fonte.
 */
function periodoFrase(frequenciaLabel: string): string {
  const norm = frequenciaLabel.trim().toLowerCase();
  switch (norm) {
    case 'semanal':
      return 'toda semana';
    case 'quinzenal':
      return 'a cada quinzena';
    case 'mensal':
      return 'todo mês';
    case 'bimestral':
      return 'a cada dois meses';
    case 'trimestral':
      return 'a cada trimestre';
    case 'semestral':
      return 'a cada semestre';
    case 'anual':
      return 'todo ano';
    case 'bienal':
      return 'a cada dois anos';
    default:
      return `a cada ${norm}`;
  }
}

export function RecurringPaymentNotice({
  method,
  frequenciaLabel,
  valorFormatado,
}: Props) {
  const periodo = periodoFrase(frequenciaLabel);
  const titulo = `Cobrança recorrente ${frequenciaLabel}`;

  // Mensagem específica por método de pagamento.
  // - PIX e Boleto: o pagador recebe uma nova fatura/link antes de cada
  //   vencimento e precisa pagar manualmente.
  // - Cartão: a cobrança é debitada automaticamente na fatura do cartão,
  //   sem ação adicional do pagador (valor renderizado em negrito).
  const Icon = method === 'card' ? RefreshCw : Info;

  // Padrão InlineNotice: sem fundo, barra lateral colorida, ícone colorido,
  // texto foreground. Cor brand-primary para destacar como informação útil
  // (não é alerta nem erro).
  return (
    <div
      className="mb-4 flex gap-2 border-l-2 border-[var(--brand-primary)] pl-2.5 py-1"
      role="note"
      aria-label={titulo}
    >
      <Icon
        className="w-3 h-3 mt-0.5 shrink-0 text-[var(--brand-primary)]"
        aria-hidden
      />
      <div className="text-[10px] leading-snug text-slate-700">
        <p className="font-bold text-slate-900 uppercase tracking-wide mb-0.5">{titulo}</p>
        {method === 'card' ? (
          <p>
            Cobrança de <strong>{valorFormatado}</strong> será debitada
            automaticamente {periodo} na fatura do cartão, sem necessidade de
            nova ação. Você pode cancelar quando quiser.
          </p>
        ) : (
          <p>
            Você receberá uma nova fatura {periodo} no valor de{' '}
            <strong>{valorFormatado}</strong>. O link de pagamento (
            {method === 'pix' ? 'PIX' : 'Boleto'}) será enviado automaticamente
            antes de cada vencimento.
          </p>
        )}
      </div>
    </div>
  );
}
